// Shell-owned page mounting: surgical replacement of remote route branches.
// The remote's exposed tree nests the page branches under its root (auth
// middleware + providers) and a pathless error boundary, so replacements
// walk the tree instead of assuming a flat list.

import { Box } from '@chakra-ui/react';
import { Outlet, type RouteObject } from 'react-router';
import RouteErrorFallback from '../components/RouteErrorFallback';
import JobsPage from '../pages/jobs/JobsPage';

// The first shell-owned page: /jobs (the list) renders the shell's own
// JobsPage, fed by the console BFF — the remote's jobs INDEX route (and its
// prefetch loader) is dropped, while the rest of the branch (the /jobs/:id
// detail) stays federated. Replacing inside the remote's branch, rather
// than shadowing with a sibling route, keeps exactly one /jobs match.
const shellJobsBranch = (remoteJobs: RouteObject): RouteObject => ({
    path: 'jobs',
    // The remote's JobsLayout is a bare <div id="jobs">; this one adds
    // height:100% so the virtualized list page can size itself to the
    // content pane (the remote's wrappers above render no DOM of their own).
    element: (
        <Box id='jobs' height='100%'>
            <Outlet />
        </Box>
    ),
    children: [
        {
            index: true,
            element: <JobsPage />,
            // The shell's shared boundary (query errors throw via the
            // client's default throwOnError, lib/trpc.ts): same chunk-error
            // auto-reload as the root fallback, sized to the content pane.
            errorElement: (
                <RouteErrorFallback
                    title='My Jobs could not be loaded.'
                    height='100%'
                />
            ),
            // The remote's document-title mechanism reads match handles;
            // shellOwned marks the page as the shell's own (tests assert
            // the branch swap by it).
            handle: { title: 'My Jobs', shellOwned: true },
        },
        ...(remoteJobs.children ?? []).filter((child) => !child.index),
    ],
});

/** Replaces the remote's `jobs` branch (wherever it nests) with the
 * shell-owned one. `found: false` means the remote reshaped its tree and
 * the mount needs revisiting — the shell treats that as fatal (router.tsx);
 * embedding hosts fall back to the unmodified tree instead.
 *
 * `wrapBranch` decorates the replaced branch — the federation expose
 * (src/expose/jobsBranch.tsx) uses it to add the providers a foreign host
 * doesn't supply. The shell itself passes nothing: its App/AppLayout already
 * provide the query client and the Toaster outlet. */
export function replaceJobsBranch(
    routes: RouteObject[],
    wrapBranch?: (branch: RouteObject) => RouteObject,
): {
    routes: RouteObject[];
    found: boolean;
} {
    let found = false;
    const walk = (list: RouteObject[]): RouteObject[] =>
        list.map((route) => {
            if (!found && route.path === 'jobs') {
                found = true;
                const branch = shellJobsBranch(route);
                return wrapBranch ? wrapBranch(branch) : branch;
            }
            if (route.children) {
                return { ...route, children: walk(route.children) };
            }
            return route;
        });
    return { routes: walk(routes), found };
}
